import { useState } from 'react';
import { ENDERECO_VAZIO, type Endereco } from '@aponte/shared';
import { api } from '../../api/cliente.js';
import { Campo } from '../../componentes/Campo.js';
import { mensagemDoErro } from '../../componentes/Aviso.js';

interface Props {
  endereco: Endereco;
  aoMudar: (endereco: Endereco) => void;
  somenteLeitura?: boolean;
}

/**
 * Endereço com busca por CEP.
 *
 * O CEP é digitado e, ao sair do campo, o serviço devolve logradouro, bairro, cidade e
 * UF. Sobram para a pessoa **número e complemento** — que nenhum serviço sabe.
 *
 * Os campos preenchidos pela consulta seguem editáveis: base de CEP erra, tem logradouro
 * genérico e desconhece endereço novo. Travar o campo transformaria um palpite do serviço
 * em verdade impossível de corrigir.
 */
export function BlocoDeEndereco({ endereco, aoMudar, somenteLeitura }: Props) {
  const [buscando, setBuscando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ultimoBuscado, setUltimoBuscado] = useState('');

  const alterar = (mudanca: Partial<Endereco>) => aoMudar({ ...endereco, ...mudanca });

  async function buscarPeloCep(cep: string) {
    const digitos = cep.replace(/\D/g, '');
    if (digitos.length !== 8 || digitos === ultimoBuscado || somenteLeitura) return;

    setBuscando(true);
    setAviso(null);
    try {
      const achado = await api.consultarCep(digitos);
      setUltimoBuscado(digitos);
      aoMudar({
        ...endereco,
        cep: digitos,
        logradouro: achado.logradouro || endereco.logradouro,
        bairro: achado.bairro || endereco.bairro,
        cidade: achado.cidade,
        uf: achado.uf,
      });
      setAviso(
        achado.logradouro
          ? 'Endereço preenchido pelo CEP. Falta o número.'
          : 'CEP de município. Preencha logradouro e número.',
      );
    } catch (erro) {
      setAviso(mensagemDoErro(erro));
    } finally {
      setBuscando(false);
    }
  }

  return (
    <>
      <div className="linha t3">
        <Campo
          rotulo="CEP"
          valor={endereco.cep}
          mascara="cep"
          placeholder="00000-000"
          somenteLeitura={somenteLeitura}
          aoMudar={(cep) => alterar({ cep })}
          aoSairDoCampo={buscarPeloCep}
          dica={buscando ? 'Consultando…' : (aviso ?? 'Sai do campo e o endereço se preenche.')}
          acao={
            <button
              type="button"
              className="bt vazado pequeno"
              disabled={buscando || somenteLeitura || endereco.cep.length !== 8}
              onClick={() => void buscarPeloCep(endereco.cep)}
            >
              {buscando ? '…' : 'Buscar'}
            </button>
          }
        />
        <Campo
          rotulo="Logradouro"
          valor={endereco.logradouro}
          placeholder="Avenida, rua, praça"
          somenteLeitura={somenteLeitura}
          aoMudar={(logradouro) => alterar({ logradouro })}
        />
        <Campo
          rotulo="Número"
          valor={endereco.numero}
          placeholder="3477"
          somenteLeitura={somenteLeitura}
          aoMudar={(numero) => alterar({ numero })}
        />
      </div>

      <div className="linha t4">
        <Campo
          rotulo="Complemento"
          valor={endereco.complemento}
          placeholder="12º andar, sala 3"
          somenteLeitura={somenteLeitura}
          aoMudar={(complemento) => alterar({ complemento })}
        />
        <Campo
          rotulo="Bairro"
          valor={endereco.bairro}
          somenteLeitura={somenteLeitura}
          aoMudar={(bairro) => alterar({ bairro })}
        />
        <Campo
          rotulo="Cidade"
          valor={endereco.cidade}
          somenteLeitura={somenteLeitura}
          aoMudar={(cidade) => alterar({ cidade })}
        />
        <Campo
          rotulo="UF"
          valor={endereco.uf}
          mascara="uf"
          placeholder="SP"
          somenteLeitura={somenteLeitura}
          aoMudar={(uf) => alterar({ uf })}
        />
      </div>
    </>
  );
}

export { ENDERECO_VAZIO };
